
import { useAuthUser } from './useAuthUser';

/**
 * Hook para verificar se o usuário atual é administrador
 * 
 * OTIMIZADO: Agora reutiliza o cache do useAuthUser em vez de fazer
 * uma query separada, eliminando roundtrip duplicado ao banco.
 * 
 * O que faz:
 * - Verifica se o usuário logado tem privilégios de administrador
 * - Gerencia estado de loading durante a verificação
 * - Retorna resultado da verificação de forma reativa
 * 
 * Onde é usado:
 * - Componentes que precisam verificar privilégios admin
 * - Páginas que têm funcionalidades específicas para admins
 * - Renderização condicional baseada em permissões
 */

interface AdminCheckResult {
  isAdmin: boolean;
  loading: boolean;
  error: string | null;
}

export const useAdminCheck = (): AdminCheckResult => {
  // 🎯 OTIMIZAÇÃO: Reutilizar cache do useAuthUser
  // Isso evita uma query duplicada ao user_profiles
  const { userProfile, loading: authLoading, profileError } = useAuthUser();

  // Verificar se é admin baseado no profile_type do perfil cacheado
  const isAdmin = userProfile?.profile_type === 'admin';

  // O loading combina o loading do auth (userProfile carregando)
  const loading = authLoading;

  // Converter erro do profile para string
  const error = profileError?.message || null;

  // Log para debug (apenas em desenvolvimento)
  if (process.env.NODE_ENV === 'development' && !loading) {
    console.log('🔐 [useAdminCheck] Status:', { isAdmin, loading, hasProfile: !!userProfile });
  }

  return {
    isAdmin,
    loading,
    error
  };
};
